import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './tufts-yext-location-cards.css.lit';
import {
  HealthcareProfessional,
  verticalSearchResponseStructure,
  verticalSearchResults,
} from '../tufts-yext/tufts-yext-types';
import {
  deleteDuplicateProfiles,
  formatPhone,
} from '../tufts-yext/tufts-yext-format';
import { OutlineElement } from '../../base/outline-element/outline-element';
import '../tufts-heading/tufts-heading';
import '../tufts-grid/tufts-grid';
import { SlotNGController } from '../../controllers/slot-ng-controller';

/**
 * The Tufts Yext Location Cards component
 *
 * @element ufts-yext-location-cards
 *
 * @slot icon
 *
 * @attr Heading
 * @attr Link text
 * @attr Search
 * @attr Search Node
 */
@customElement('tufts-yext-location-cards')
export class TuftsYextLocationsCards extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  ngSlot = new SlotNGController(this);

  /**
   * Heading text render
   */
  @property({ type: String, reflect: true, attribute: 'heading' })
  heading: string;
  /**
   * Text link render
   */
  @property({ type: String, reflect: true, attribute: 'link-text' })
  linkText: string;
  /**
   * Search service yext
   */
  @property({ type: String, reflect: true, attribute: 'search-service' })
  search: string = '';
  /**
   * Search service yext
   */
  @property({ type: String, reflect: true, attribute: 'search-node' })
  searchNode: string = '';

  @state()
  locationResults: verticalSearchResults = [];

  experienceKey = 'tufts-fal';
  verticalKey = 'healthcare_facilities';
  urlHref = 'https://cdn.yextapis.com/v2/accounts';
  accountId = 'me';
  contentType = 'search/vertical/query';
  apiKey = 'f70a1166dcbde8731713f74b5d21b86f';
  urlBase = `${this.urlHref}/${this.accountId}/${this.contentType}`;
  apiVersion = '20230406';
  apiVersionEntities = '20230301';
  version = 'PRODUCTION';
  locale = 'en';

  connectedCallback() {
    super.connectedCallback();
    this.fetchLocationFromYext();
  }

  fetchLocationFromYext() {
    const params = new URLSearchParams();
    params.set('v', this.apiVersion);
    params.set('api_key', this.apiKey);
    params.set('experienceKey', this.experienceKey);
    params.set('verticalKey', this.verticalKey);
    params.set('version', this.version);
    params.set('locale', this.locale);
    params.set('retrieveFacets', 'true');
    params.set('limit', '4');
    params.set('input', this.search.toLocaleLowerCase());

    const url = `${this.urlBase}?${params.toString()}`;

    fetch(url)
      .then(response => response.json())
      .then(
        (locationResponse: {
          meta: {};
          response: verticalSearchResponseStructure;
        }) => {
          // Check if there are results in the locationResults array.

          if (
            locationResponse.response.results &&
            locationResponse.response.results.length <= 0
          ) {
            this.checkParentNodeAndUpdate();
            return;
          }
          deleteDuplicateProfiles(locationResponse);
          this.locationResults = locationResponse.response.results;
        }
      )
      .catch(error => {
        console.error(error);
        this.checkParentNodeAndUpdate();
      });
  }
  /**
   * Checks the parent node for a background-color attribute
   * and updates it if there are no results
   *
   */

  checkParentNodeAndUpdate() {
    // If there are no results in the array, look for the closest ancestor with the 'tufts-container' tag.
    const parentElement = this.closest('tufts-container') as Element;

    // If a 'tufts-container' ancestor is found, set its 'background-color' attribute to 'white'.
    if (parentElement) {
      parentElement.setAttribute('background-color', 'white');
    }
  }
  tuftsCardTemplate(location: HealthcareProfessional) {
    const addressString = location.address
      ? `${location.address.line1 || ''} ${location.address.city || ''}, ${location.address.region || ''} ${location.address.postalCode || ''}`
      : null;
    const formattedMainPhone = formatPhone(location.mainPhone);
    return html`
      <div class="show-border ">
        <p>
          <a
            href="/get-care/locations/${location.name}"
            data-analytics="VIEW_WEBSITE"
            >${location.name}</a
          >
        </p>
        <p>${addressString}</p>
        <div class="phone">
          <span class="visually-hidden">Phone Number</span>
          <a data-analytics="TAP_TO_CALL" href="tel:${location.mainPhone}"
            >${formattedMainPhone}</a
          >
        </div>
      </div>
    `;
  }
  renderHeadingWithLink(): TemplateResult | null {
    if (!this.heading && !this.linkText) {
      return null;
    }

    const searchNodeParam = this.searchNode
      ? `&f[0]=services:${this.searchNode}`
      : '';

    return html`
      <div class="heading-with-link">
        <tufts-heading level-size="xl" class="block">
          <h2>${this.heading}</h2>
        </tufts-heading>
        <tufts-button
          button-variant="link"
          button-url="/get-care/locations?search_api_fulltext=${searchNodeParam}"
          button-label="${this.linkText}"
        >
          ${this.linkText}
        </tufts-button>
      </div>
    `;
  }

  render(): TemplateResult {
    if (this.locationResults.length === 0) {
      return html``;
    }

    return html`
      ${this.ngSlot.conditionalNGSlot('icon')} ${this.renderHeadingWithLink()}
      <tufts-grid columns="4" item-min-width="250px">
        ${this.locationResults.map(
          location => html` ${this.tuftsCardTemplate(location.data)} `
        )}
      </tufts-grid>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-yext-location-cards': TuftsYextLocationsCards;
  }
}
